// Co-living errors come in three shapes:
//   - a business error:   { response: { status, data: { detail: { code, message, ...extra } } } }
//   - a validation error: { response: { status, data: { detail: [ {loc, msg, ...} ] } } }
//   - an auth error:      { response: { status, data: { detail: 'plain string' } } }
// The proxy itself answers 502/503 with `{ error: '...' }` and no detail.
// `message` is English for logs; callers map `code` to their own i18n copy.

export function apiErrorCode(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (!err?.response) return 'network';
  if (detail == null) {
    // nginx or the proxy refuses an oversized upload before singabot sees it.
    if (status === 413) return 'file_too_large';
    return status === 502 || status === 503 ? 'proxy_unavailable' : 'unknown';
  }
  if (Array.isArray(detail)) return 'validation';
  if (typeof detail === 'string') {
    return status === 401 ? 'unauthorized' : 'forbidden';
  }
  return detail.code || 'unknown';
}

// A failed download (`responseType: 'blob'`) carries its JSON error body as a
// Blob, which apiErrorCode cannot read. Parse it back into `response.data`;
// anything that is not JSON is left as it was.
const blobText = blob =>
  typeof blob.text === 'function'
    ? blob.text()
    : new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(reader.error);
        reader.readAsText(blob);
      });

export async function readBlobError(err) {
  const body = err?.response?.data;
  if (typeof Blob === 'undefined' || !(body instanceof Blob)) return err;
  try {
    err.response.data = JSON.parse(await blobText(body));
  } catch (e) {
    // not JSON (a proxy page): keep the original error
  }
  return err;
}

// Extra keys sit next to `code` (`used_by`, `allowed_steps`, `weekday`, ...).
export function apiErrorDetail(err) {
  const detail = err?.response?.data?.detail;
  return detail && typeof detail === 'object' && !Array.isArray(detail)
    ? detail
    : {};
}

// Looks up CO_LIVING.ERRORS.<CODE> in i18n, then `fallbackKey`, then the
// generic message. `vm` is any component instance (has $t/$te).
export function apiErrorMessage(vm, err, fallbackKey) {
  const i18nKey = `CO_LIVING.ERRORS.${apiErrorCode(err).toUpperCase()}`;
  if (vm.$te(i18nKey)) {
    return vm.$t(i18nKey);
  }
  if (fallbackKey && vm.$te(fallbackKey)) {
    return vm.$t(fallbackKey);
  }
  return vm.$t('CO_LIVING.ERRORS.UNKNOWN');
}
